/* 📖 Regionize v0.1.7 */
const div = (cls) => {
    const el = document.createElement('div');
    el.classList.add(cls);
    return el;
};
class Region {
    constructor(el) {
        this.suppressErrors = false;
        this.element = el;
        this.content = div('region-content');
        this.content.style.position = 'relative';
        this.element.appendChild(this.content);
        this.path = [];
    }
    setPath(newPath) {
        this.path = newPath;
        if (newPath.length > 0)
            this.content.appendChild(newPath[0]);
    }
    get currentElement() {
        const len = this.path.length;
        if (len > 0)
            return this.path[len - 1];
        return this.content;
    }
    isEmpty() {
        const el = this.content;
        if (el.textContent === null)
            return true;
        return el.textContent.trim() === '' && el.offsetHeight < 2;
    }
    isReasonableSize() {
        const box = this.element.getBoundingClientRect();
        return box.height > 100 && box.width > 100; // TODO: Number is arbitrary
    }
    overflowAmount() {
        const contentH = this.content.offsetHeight;
        const boxH = this.element.offsetHeight;
        if (boxH === 0)
            throw Error('Regionizer: Trying to flow into an element with zero height.');
        return contentH - boxH;
    }
    hasOverflowed() {
        return this.overflowAmount() > -5;
    }
}

const isTextNode = (node) => {
    return node.nodeType === Node.TEXT_NODE;
};
const isElement = (node) => {
    return node.nodeType === Node.ELEMENT_NODE;
};
const isScript = (node) => {
    return node.tagName === 'SCRIPT';
};
const isImage = (node) => {
    return node.tagName === 'IMG';
};
const isUnloadedImage = (node) => {
    return isImage(node) && !node.naturalWidth;
};
const isContentElement = (node) => {
    return isElement(node) && !isScript(node);
};

const MAX_TIME = 30; // ms
const rAF = () => new Promise(resolve => {
    requestAnimationFrame(t => resolve(t));
});
let lastYieldTime = 0;
const shouldYield = () => {
    const timeSinceYield = performance.now() - lastYieldTime;
    return timeSinceYield > MAX_TIME;
};
const yieldIfNecessary = async () => {
    if (shouldYield())
        lastYieldTime = await rAF();
};

const overflowAttr = 'data-ignore-overflow';
// Walk up the tree to see if we are within
// an overflow-ignoring node
const ignoreOverflow = (element) => {
    if (element.hasAttribute(overflowAttr))
        return true;
    if (element.parentElement)
        return ignoreOverflow(element.parentElement);
    return false;
};

const SPACE = ' ';
const nextWordEnd = (text, startIndex) => {
    let newIndex = startIndex + 1;
    while (newIndex < text.length && text.charAt(newIndex) !== SPACE) {
        newIndex += 1;
    }
    return newIndex;
};
const previousWordEnd = (text, startIndex) => {
    let newIndex = startIndex;
    if (text.charAt(newIndex) === SPACE) {
        newIndex -= 1;
    }
    while (text.charAt(newIndex) !== SPACE && newIndex > 0) {
        newIndex -= 1;
    }
    return newIndex;
};

const createTextNode = (text) => document.createTextNode(text);
// Try adding a text node in one go.
// Returns true if all the text fits, false if none fits.
const addInOneGo = async (textNode, container, hasOverflowed) => {
    container.appendChild(textNode);
    const success = !hasOverflowed();
    if (!success)
        container.removeChild(textNode);
    await yieldIfNecessary();
    return { completed: success };
};
// Incrementally add words to the container until it just barely doesnt
// overflow. Returns a remainder textNode for remaining text.
const fillWordsUntilOverflow = async (textNode, container, hasOverflowed) => {
    const originalText = textNode.nodeValue || '';
    container.appendChild(textNode);
    if (!hasOverflowed() || ignoreOverflow(container)) {
        // The whole thing fits
        return { completed: true };
    }
    // Clear the node
    let proposedEnd = 0;
    textNode.nodeValue = originalText.substr(0, proposedEnd);
    while (!hasOverflowed() && proposedEnd < originalText.length) {
        // Reveal the next word
        proposedEnd = nextWordEnd(originalText, proposedEnd);
        if (proposedEnd < originalText.length) {
            textNode.nodeValue = originalText.substr(0, proposedEnd);
            await yieldIfNecessary();
        }
    }
    // Back out to word boundary
    const wordEnd = previousWordEnd(originalText, proposedEnd);
    if (wordEnd < 1) {
        // We didn't even add a complete word, don't add node
        textNode.nodeValue = originalText;
        container.removeChild(textNode);
        return { completed: false };
    }
    // trim text to word
    const fittingText = originalText.substr(0, wordEnd);
    const overflowingText = originalText.substr(wordEnd);
    textNode.nodeValue = fittingText;
    // Create a new text node for the next flow box
    return {
        completed: true,
        remainder: createTextNode(overflowingText),
    };
};
// Fills text across multiple elements by requesting a continuation
// once the current element overflows
const fillWords = async (textNode, container, getNextContainer, hasOverflowed) => {
    const textLayout = await fillWordsUntilOverflow(textNode, container, hasOverflowed);
    if (textLayout.remainder) {
        const nextContainer = getNextContainer();
        return fillWords(textLayout.remainder, nextContainer, getNextContainer, hasOverflowed);
    }
    return textLayout;
};

// Shifts this element to the next page. If any of its
// ancestors cannot be split across page, it will
// step up the tree to find the first ancestor
// that can be split, and move all of that descendants
// to the next page.
const tryInNextRegion = (region, makeNextRegion, canSplit) => {
    if (region.path.length <= 1) {
        throw Error('Regionize: Attempting to move the top-level element');
    }
    const startLength = region.path.length;
    // So this node won't get cloned. TODO: this is unclear
    const elementToMove = region.path.pop();
    // find the nearest splittable parent
    let nearestMoveableElement = elementToMove;
    const pathToRestore = [];
    while (region.path.length > 1 && !canSplit(region.currentElement)) {
        nearestMoveableElement = region.path.pop();
        pathToRestore.unshift(nearestMoveableElement);
    }
    // Once a node is moved to a new page, it should no longer trigger another
    // move. otherwise tall elements will endlessly get shifted to the next page
    nearestMoveableElement.setAttribute('data-regionize-did-move', 'true');
    const parent = nearestMoveableElement.parentNode;
    parent.removeChild(nearestMoveableElement);
    // If the nearest ancestor would be empty without this node,
    // move it to the next page too.
    if (region.path.length > 1 &&
        region.currentElement.textContent.trim() === '') {
        parent.appendChild(nearestMoveableElement);
        nearestMoveableElement = region.path.pop();
        pathToRestore.unshift(nearestMoveableElement);
        nearestMoveableElement.parentNode.removeChild(nearestMoveableElement);
    }
    let nextRegion;
    if (!region.isEmpty()) {
        if (region.hasOverflowed()) {
            // Recovery failed, maybe the box contains a large
            // unsplittable element.
            region.suppressErrors = true;
        }
        nextRegion = makeNextRegion();
    }
    else {
        // If the page is empty when this node is removed,
        // then it won't help to move it to the next page.
        // Instead continue here until the node is done.
        nextRegion = region;
    }
    // append moved node as first in new page
    nextRegion.currentElement.appendChild(nearestMoveableElement);
    // restore subpath
    pathToRestore.forEach(r => nextRegion.path.push(r));
    nextRegion.path.push(elementToMove);
    if (startLength !== nextRegion.path.length) {
        throw Error('Regionize: Restored path depth does not match original path depth');
    }
};

// The path is an array of nested elments,
// for example .content > article > p > a).
//
// It's shallowly cloned every time we move to the next page,
// to create the illusion that nodes are continuing from page
// to page.
//
// The transition can be customized by setting a Split rule,
// which lets you add classes to the original and cloned element
// to customize styling.
const clone = (el, withChildren) => {
    return el.cloneNode(withChildren);
};
const shallowClone = (el) => clone(el, false);
const deepClone = (el) => clone(el, true);
const clonePath = (oldPath, applyRules) => {
    const newPath = [];
    const deepCloneWithRules = (el) => {
        const clone = deepClone(el); // could be th > h3 > span;
        applyRules(el, clone);
        return clone;
    };
    for (let i = oldPath.length - 1; i >= 0; i -= 1) {
        const original = oldPath[i];
        const clone = shallowClone(original);
        const nextChild = oldPath[i + 1];
        clone.innerHTML = '';
        applyRules(original, clone, nextChild, deepCloneWithRules);
        if (i < oldPath.length - 1)
            clone.appendChild(newPath[i + 1]);
        newPath[i] = clone;
    }
    return newPath;
};

// Polls every 10ms for image.naturalWidth
// or an error event.
//
// Note: Doesn't ever reject, since missing images
// shouldn't prevent layout from resolving
const wait10 = () => new Promise(resolve => {
    setTimeout(() => {
        resolve();
    }, 10);
});
const ensureImageLoaded = async (image) => {
    const imgStart = performance.now();
    let failed = false;
    image.addEventListener('error', () => {
        failed = true;
    });
    image.src = image.src; // re-trigger error if already failed
    while (!image.naturalWidth && !failed) {
        await wait10();
    }
    return performance.now() - imgStart;
};

const preserveNumbering = (original, clone, nextChild) => {
    // restart numbering
    let prevStart = 1;
    if (original.hasAttribute('start')) {
        // the OL is also a continuation
        prevStart = parseInt(original.getAttribute('start') || '', 10);
    }
    if (nextChild && nextChild.tagName === 'LI') {
        // the first list item is a continuation
        prevStart -= 1;
    }
    const prevCount = original.children.length;
    const newStart = prevStart + prevCount;
    clone.setAttribute('start', `${newStart}`);
};

const preserveTableColumns = (original, clone, nextChild, deepClone) => {
    const columns = [...original.children];
    const currentIndex = columns.indexOf(nextChild);
    for (let i = 0; i < currentIndex; i += 1) {
        const origCol = columns[i];
        if (origCol) {
            const clonedCol = deepClone(origCol);
            clone.appendChild(clonedCol);
        }
    }
};

const noop = () => { };
const always = () => true;
const never = () => false;
// flow content through FlowBoxes.
// the caller is responsible for managing
// and creating regions.
const flowIntoRegions = async (opts) => {
    var _a, _b, _c, _d, _e, _f;
    const content = opts.content;
    const createRegion = opts.createRegion;
    if (!content)
        throw Error('content not specified');
    if (!createRegion)
        throw Error('createRegion not specified');
    // optional
    const applySplit = (_a = opts.applySplit) !== null && _a !== void 0 ? _a : noop;
    const canSplit = (_b = opts.canSplit) !== null && _b !== void 0 ? _b : always;
    const beforeAdd = (_c = opts.beforeAdd) !== null && _c !== void 0 ? _c : noop;
    const afterAdd = (_d = opts.afterAdd) !== null && _d !== void 0 ? _d : noop;
    const didWaitFor = (_e = opts.didWaitFor) !== null && _e !== void 0 ? _e : noop;
    const shouldTraverse = (_f = opts.shouldTraverse) !== null && _f !== void 0 ? _f : never;
    // currentRegion should hold the only state that persists during traversal.
    let currentRegion = createRegion();
    const hasOverflowed = () => currentRegion.hasOverflowed();
    const canSplitCurrent = () => canSplit(currentRegion.currentElement);
    const ignoreCurrentOverflow = () => ignoreOverflow(currentRegion.currentElement);
    const splitRules = (original, clone, nextChild, deepClone) => {
        if (original.tagName === 'OL') {
            preserveNumbering(original, clone, nextChild);
        }
        if (original.tagName === 'TR' && nextChild && deepClone) {
            preserveTableColumns(original, clone, nextChild, deepClone);
        }
        applySplit(original, clone, nextChild, deepClone);
    };
    const continueInNextRegion = () => {
        const prevRegion = currentRegion;
        currentRegion = createRegion();
        const newPath = clonePath(prevRegion.path, splitRules);
        currentRegion.setPath(newPath);
        return currentRegion;
    };
    const continuedParent = () => {
        continueInNextRegion();
        return currentRegion.currentElement;
    };
    const addText = async (textNode, isSplittable) => {
        const el = currentRegion.currentElement;
        let textLayout;
        if (isSplittable) {
            // Add the text word by word, adding pages as needed
            textLayout = await fillWords(textNode, el, continuedParent, hasOverflowed);
            if (!textLayout.completed && currentRegion.path.length > 1) {
                tryInNextRegion(currentRegion, continueInNextRegion, canSplit);
                textLayout = await fillWords(textNode, el, continuedParent, hasOverflowed);
            }
        }
        else {
            // Add the text as a block, trying a new page if needed
            textLayout = await addInOneGo(textNode, currentRegion.currentElement, hasOverflowed);
            if (!textLayout.completed && !ignoreCurrentOverflow()) {
                tryInNextRegion(currentRegion, continueInNextRegion, canSplit);
                textLayout = await addInOneGo(textNode, currentRegion.currentElement, hasOverflowed);
            }
        }
        // Something went wrong. Insert the text anyways, ignoring overflow,
        // and move onto the next region.
        if (!textLayout.completed) {
            currentRegion.currentElement.appendChild(textNode);
            if (!ignoreCurrentOverflow() && canSplitCurrent()) {
                currentRegion.suppressErrors = true;
                continueInNextRegion();
            }
        }
    };
    const shouldTraverseChildren = (element) => {
        if (hasOverflowed())
            return true;
        if (element.querySelector('img'))
            return true;
        if (shouldTraverse(element))
            return true;
        return false;
    };
    const addElement = async (element) => {
        // Ensure images are loaded before testing for overflow
        if (isUnloadedImage(element)) {
            const waitTime = await ensureImageLoaded(element);
            didWaitFor(waitTime);
        }
        // Transforms before adding
        await beforeAdd(element, continueInNextRegion);
        // Append element and push onto the the stack
        currentRegion.currentElement.appendChild(element);
        currentRegion.path.push(element);
        if (shouldTraverseChildren(element)) {
            // Only if the region overflowed, the content contains
            // an image, or the caller has requested a custom traversal.
            await clearAndAddChildren(element);
        }
        // We're done: Pop off the stack and do any cleanup
        const addedElement = currentRegion.path.pop();
        await afterAdd(addedElement, continueInNextRegion);
    };
    const clearAndAddChildren = async (element) => {
        const childNodes = [...element.childNodes];
        element.innerHTML = '';
        if (hasOverflowed() && !ignoreCurrentOverflow() && canSplitCurrent()) {
            // Overflows when empty
            tryInNextRegion(currentRegion, continueInNextRegion, canSplit);
        }
        const shouldSplit = canSplit(element) && !ignoreOverflow(element);
        for (const child of childNodes) {
            if (isTextNode(child)) {
                await addText(child, shouldSplit);
            }
            else if (isContentElement(child)) {
                await addElement(child);
            }
        }
    };
    return addElement(content);
};

export { Region, flowIntoRegions };
